import type { SSEEvents } from '@zyno-platform/shared';

import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source';
import { isEqual } from 'lodash';
import { onMounted, onUnmounted, ref } from 'vue';

import { Logger } from './logger';

const logger = Logger.createScoped('SSE');

const InitialRetryDelayMs = 2_000;
const MaxRetryDelayMs = 30_000;

export class SSEService {
    /**
     * Bumped by every teardown (disconnect, or a 401 we couldn't recover from). `connect()` captures
     * it on entry and bails at every point it could resume after an await, so a connect attempt that
     * was already in flight when the session ended can't re-open a stream or schedule a reconnect.
     * This is the only thing that says "a connection is wanted" — do not reintroduce a plain boolean
     * flag, since an in-flight connect would set it back to true after the session was torn down.
     */
    private static generation = 0;
    private static ctrl: AbortController | null = null;
    /** Every controller whose request is still in flight — a handover keeps two alive at once. */
    private static liveCtrls = new Set<AbortController>();
    private static connectionHeaders: Record<string, string> | null = null;
    private static connectionPromise: Promise<void> | null = null;
    private static retryDelayMs = InitialRetryDelayMs;
    private static subscriptionCache: Record<string, Set<(data: unknown) => void>> = {};
    static connId: string | null = null;
    /** Reactive: true while a live SSE connection is open, false while down/reconnecting. */
    static isConnected = ref(false);

    static async connect() {
        const generation = this.generation;

        const [{ getApiHeaders }, { Auth }] = await Promise.all([import('../../openapi-clients/shared'), import('./auth.service')]);
        if (generation !== this.generation) return;

        const sseUrl = `${import.meta.env.VITE_APP_AUTH_API_URL}/sse`;
        const allHeaders = await getApiHeaders({ method: 'GET', url: sseUrl, headers: {} }, true);
        // The session may have ended (idle timeout, logout) while we were resolving auth headers.
        if (generation !== this.generation) return;

        const headers = Object.fromEntries(Object.entries(allHeaders).filter(([k]) => /^(authorization|x-zs-|x-uxrr-sid$)/i.test(k)));
        const authorization = Object.entries(headers).find(([key]) => key.toLowerCase() === 'authorization')?.[1];
        const requestedJwt = authorization ? (/^Bearer\s+(.+)$/i.exec(authorization)?.[1] ?? null) : null;
        const requestedJwtExpiresAt = Auth.activeJwtExp;

        if (isEqual(this.connectionHeaders, headers)) return;
        this.connectionHeaders = headers;

        const existingAbortCtrl = this.ctrl;
        const ctrl = new AbortController();
        this.ctrl = ctrl;
        this.liveCtrls.add(ctrl);

        logger.info('Connecting to SSE endpoint');

        let thisConnId: string | null = null;
        let rejectWithoutRetry = false;
        try {
            this.connectionPromise = fetchEventSource(sseUrl, {
                openWhenHidden: true,
                signal: ctrl.signal,
                headers,
                onopen: async response => {
                    if (!response.ok) {
                        rejectWithoutRetry = true;
                        if (response.status === 401) {
                            logger.warn('SSE request was unauthorized; recovering browser session');
                            const recovered = await Auth.recoverUnauthorizedJwt(requestedJwt, requestedJwtExpiresAt);
                            if (!recovered) {
                                // There is no session left to reconnect with — anything we retry would
                                // 401 again, so stop the loop instead of hammering the auth API.
                                logger.warn('SSE session could not be recovered; stopping reconnects');
                                this.teardown();
                            }
                        }
                        throw new Error(`SSE endpoint returned ${response.status} ${response.statusText}`.trim());
                    }

                    existingAbortCtrl?.abort();

                    const connId = response.headers.get('x-zs-scid');
                    logger.info('Connected to SSE endpoint', { connId });
                    thisConnId = connId;
                    this.connId = connId;
                    this.isConnected.value = true;
                    this.retryDelayMs = InitialRetryDelayMs;
                },
                onmessage: event => {
                    if (this.connId === thisConnId) {
                        this.handleMessage(event);
                    }
                },
                onerror: err => {
                    if (rejectWithoutRetry || this.connId === thisConnId) {
                        // we need to throw the error to bypass the auto-retry
                        // we need to bypass auto-retry so that we can get fresh auth headers
                        throw err;
                    }
                }
            });
            await this.connectionPromise;
            logger.info('Disconnected from SSE endpoint', { connId: thisConnId });
        } catch (err) {
            logger.error('Error in SSE connection:', err);
        } finally {
            this.liveCtrls.delete(ctrl);
            if (this.ctrl === ctrl) this.ctrl = null;
            this.connectionPromise = null;
            this.connectionHeaders = null;
        }

        // A newer connection took over — it owns the reconnect loop from here.
        if (this.connId !== thisConnId) return;

        // The current connection dropped (and no newer one took over) — reflect
        // that until the reconnect below re-opens it.
        this.connId = null;
        this.isConnected.value = false;

        // Torn down while we were connected (logout, idle timeout, unrecoverable 401): stay down.
        if (generation !== this.generation) return;

        const delayMs = this.retryDelayMs;
        this.retryDelayMs = Math.min(delayMs * 2, MaxRetryDelayMs);
        setTimeout(() => {
            if (generation !== this.generation) return;
            this.connect();
        }, delayMs);
    }

    static async disconnect() {
        const pendingConnection = this.connectionPromise;
        this.teardown();
        // The connect() that owns this promise logs its own failure; we only need it settled.
        await pendingConnection?.catch(() => {});
        this.isConnected.value = false;
    }

    /** Invalidate every in-flight/scheduled connection attempt and drop the live streams. */
    private static teardown() {
        this.generation++;
        this.retryDelayMs = InitialRetryDelayMs;
        this.connectionHeaders = null;
        this.connId = null;
        for (const ctrl of this.liveCtrls) ctrl.abort();
        this.liveCtrls.clear();
        this.ctrl = null;
        this.isConnected.value = false;
    }

    // oxlint-disable-next-line no-explicit-any
    static subscribe(event: string, handler: (data: any) => void) {
        this.subscriptionCache[event] = this.subscriptionCache[event] ?? new Set();
        this.subscriptionCache[event].add(handler);
    }

    // oxlint-disable-next-line no-explicit-any
    static unsubscribe(event: string, handler: (data: any) => void) {
        this.subscriptionCache[event]?.delete(handler);
    }

    static createEventChannel<T extends keyof SSEEvents>(eventName: T) {
        return {
            subscribe(listener: (data: SSEEvents[T]) => void) {
                SSEService.subscribe(eventName, listener);
            },

            unsubscribe(listener: (data: SSEEvents[T]) => void) {
                SSEService.unsubscribe(eventName, listener);
            },

            use(listener: (data: SSEEvents[T]) => void) {
                onMounted(() => this.subscribe(listener));
                onUnmounted(() => this.unsubscribe(listener));
            }
        };
    }

    private static handleMessage(event: EventSourceMessage) {
        const subscriptions = this.subscriptionCache[event.event];
        if (subscriptions) {
            let data: unknown = null;
            try {
                data = event.data ? JSON.parse(event.data) : null;
            } catch (err) {
                logger.error('Error parsing SSE message data:', err, { event });
            }
            for (const handler of subscriptions) {
                try {
                    handler(data);
                } catch (err) {
                    logger.error('Error in SSE message handler:', err, { event });
                }
            }
        }
    }
}
